/**
 * ListsPage - Main lists view
 *
 * Displays all user lists with card counts, values, and owned percentage.
 * Allows creating new lists and navigating to list detail view.
 */
import { useMemo, useState } from 'react';

import clsx from 'clsx';
import { useAtomValue } from 'jotai';
import { FaChevronRight, FaFileExport, FaFileImport, FaPlus, FaSearch } from 'react-icons/fa';

import useUserSettings from '@/features/settings/hooks/api';
import { formatPrice } from '@/lib/price';
import { IoWarningOutline } from 'react-icons/io5';
import { Link } from 'react-router';

import Button from '@/components/Button/Button';
import EmptyState from '@/components/EmptyState/EmptyState';
import type { SortDirection } from '@/components/SortBySelector/SortBySelector';
import SortBySelector from '@/components/SortBySelector/SortBySelector';
import Spinner from '@/components/Spinner/Spinner';

import { useDeckNeeds } from '@/hooks/useDeckNeeds';
import CreateListModal from './components/CreateListModal';
import ExportAllListsModal from './components/ExportAllListsModal';
import ImportListModal from './components/ImportListModal';
import ImportListsModal from './components/ImportListsModal';
import ListCard from './components/ListCard';
import { useLists } from './hooks/api';
import { type ListStats, listStatsByListAtom } from './state/enrichedListCards';

const EMPTY_STATS: ListStats = { cardCount: 0, totalCopies: 0, totalPrice: 0, unpricedCount: 0, ownedCount: 0 };

type ListSortField = 'updated' | 'name' | 'card_count' | 'value';

interface SortState {
  field: ListSortField;
  direction: SortDirection;
}

const SORT_OPTIONS: { value: ListSortField; label: string }[] = [
  { value: 'updated', label: 'Updated' },
  { value: 'name', label: 'Name' },
  { value: 'card_count', label: 'Cards' },
  { value: 'value', label: 'Value' },
];

// ============================================================================
// Component
// ============================================================================

export default function ListsPage() {
  const { settings } = useUserSettings();
  const { data: lists, isLoading } = useLists();
  const statsByList = useAtomValue(listStatsByListAtom);
  const { uniqueCardsNeeded, totalNeeded, totalValue, isLoading: deckNeedsLoading } = useDeckNeeds();

  const [search, setSearch] = useState('');
  const [sortState, setSortState] = useState<SortState>({ field: 'updated', direction: 'desc' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isMultiListImportOpen, setIsMultiListImportOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);

  // Compute cards per list for display
  const statsFor = (listId: string): ListStats => statsByList.get(listId) ?? EMPTY_STATS;

  // Filter and sort lists
  const filteredLists = useMemo(() => {
    if (!lists) return [];

    let result = [...lists];

    // Filter by search
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      result = result.filter((l) => l.name.toLowerCase().includes(searchLower));
    }

    // Sort
    const multiplier = sortState.direction === 'asc' ? 1 : -1;
    result.sort((a, b) => {
      switch (sortState.field) {
        case 'name':
          return multiplier * a.name.localeCompare(b.name);
        case 'card_count':
          return multiplier * ((statsByList.get(a.id)?.cardCount ?? 0) - (statsByList.get(b.id)?.cardCount ?? 0));
        case 'value':
          return multiplier * ((statsByList.get(a.id)?.totalPrice ?? 0) - (statsByList.get(b.id)?.totalPrice ?? 0));
        case 'updated':
        default: {
          const aTime = a.updated_at?.getTime?.() ?? 0;
          const bTime = b.updated_at?.getTime?.() ?? 0;
          return multiplier * (aTime - bTime);
        }
      }
    });

    return result;
  }, [lists, search, sortState, statsByList]);

  // Compute totals
  const totals = useMemo(() => {
    if (!lists) return { count: 0, cards: 0, value: 0 };
    return {
      count: lists.length,
      cards: lists.reduce((sum, l) => sum + (statsByList.get(l.id)?.cardCount ?? 0), 0),
      value: lists.reduce((sum, l) => sum + (statsByList.get(l.id)?.totalPrice ?? 0), 0),
    };
  }, [lists, statsByList]);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-zinc-800 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Lists</h1>
          {lists && lists.length > 0 && (
            <p className="mt-0.5 text-sm text-zinc-500">
              {filteredLists.length} {filteredLists.length === 1 ? 'list' : 'lists'}
              {filteredLists.length !== lists.length && ` of ${lists.length} total`}
              {totals.value > 0 && ` · ${formatPrice(totals.value, settings.currency)}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button icon={<FaPlus />} text="New List" color="primary" onClick={() => setIsCreateModalOpen(true)} />
          <Button icon={<FaFileImport />} text="Import" color="secondary" onClick={() => setIsImportModalOpen(true)} />
          <Button
            icon={<FaFileExport />}
            text="Export All"
            color="secondary"
            onClick={() => setIsExportModalOpen(true)}
            disabled={totals.count === 0}
          />
        </div>
      </header>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-zinc-800/50 bg-zinc-900/30 px-6 py-3">
        {/* Search */}
        <div className="relative max-w-75 min-w-50 flex-1">
          <FaSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-sm text-zinc-500" />
          <input
            type="text"
            placeholder="Search lists..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-zinc-700/50 bg-zinc-800/50 py-2 pr-3 pl-9 text-sm text-zinc-200 placeholder-zinc-500 focus:border-zinc-600 focus:outline-none"
          />
        </div>

        {/* Sort */}
        <SortBySelector
          value={sortState.field}
          direction={sortState.direction}
          options={SORT_OPTIONS}
          onChange={(field, direction) => setSortState({ field, direction })}
        />
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : filteredLists.length === 0 ? (
          <EmptyState
            title={search ? 'No lists found' : 'No lists yet'}
            description={search ? 'Try adjusting your search' : 'Create your first list to start tracking cards you want'}
            action={!search ? <Button text="Create List" icon={<FaPlus />} onClick={() => setIsCreateModalOpen(true)} /> : undefined}
          />
        ) : (
          <div className="space-y-6">
            {/* Deck Needs - Special computed list */}
            {!search && (
              <Link
                to="/lists/deck-needs"
                className={clsx(
                  'group flex items-center gap-4 rounded-xl p-4 transition-colors',
                  'border border-amber-500/20 bg-amber-500/10 hover:border-amber-500/30 hover:bg-amber-500/15',
                )}
              >
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-amber-500/20 text-amber-400">
                  <IoWarningOutline className="h-6 w-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-amber-200">Deck Needs</h3>
                  <p className="text-sm text-amber-400/70">
                    {deckNeedsLoading ? (
                      'Loading...'
                    ) : uniqueCardsNeeded > 0 ? (
                      <>
                        {uniqueCardsNeeded} cards needed
                        {totalNeeded > uniqueCardsNeeded && ` (${totalNeeded} copies)`}
                        {totalValue > 0 && ` · ~${formatPrice(totalValue, settings.currency)}`}
                      </>
                    ) : (
                      'All deck cards owned!'
                    )}
                  </p>
                </div>
                <FaChevronRight className="h-4 w-4 shrink-0 text-amber-500/50 transition-transform group-hover:translate-x-1" />
              </Link>
            )}

            {/* Regular lists grid */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredLists.map((list) => (
                <ListCard key={list.id} list={list} stats={statsFor(list.id)} />
              ))}

              {/* Add new card placeholder */}
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className={clsx(
                  'flex h-36 flex-col items-center justify-center gap-2 rounded-xl',
                  'border-2 border-dashed border-zinc-700/50 hover:border-zinc-600',
                  'text-zinc-500 transition-colors hover:text-zinc-400',
                )}
              >
                <FaPlus className="h-5 w-5" />
                <span className="text-sm font-medium">Create List</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <CreateListModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />

      {/* Import Modal — Universal format */}
      <ImportListModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onMultiListDetected={() => {
          setIsImportModalOpen(false);
          setIsMultiListImportOpen(true);
        }}
      />

      {/* Multi-List Import Modal — MtgCraft multi-list backup only */}
      <ImportListsModal isOpen={isMultiListImportOpen} onClose={() => setIsMultiListImportOpen(false)} />

      {/* Export Modal */}
      <ExportAllListsModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} />
    </div>
  );
}
